import type { BillingSummary } from '@snitch/contract'
import { daysLeft } from '@snitch/domain'
import { orpc } from './api.ts'

// Kept for an hour unused: going back home from a long chat shouldn't wait on the network,
// which an upload may be holding up.
export const summaryQuery = orpc.billing.summary.queryOptions({
  staleTime: 15_000,
  gcTime: 60 * 60_000,
})
export const catalogQuery = orpc.pricing.catalog.queryOptions({
  staleTime: 5 * 60_000,
  gcTime: 60 * 60_000,
})

const dayMonth = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  timeZone: 'Europe/Moscow',
})

/** "12 июня", in Moscow time — the time the whole product speaks. */
export const formatDay = (date: Date) => dayMonth.format(date)

/** One key per purchase attempt, so a retried request never makes a second order. */
export const newIdempotencyKey = () => crypto.randomUUID()

export type HeroState = 'trial' | 'active' | 'ending' | 'expired'

/** What the home hero shows, derived from the summary. Null when there's no subscription yet. */
export function heroOf(summary: BillingSummary, now = new Date()) {
  const s = summary.subscription
  if (s.status === 'none') return null
  if (s.status === 'expired' || !s.paidUntil) {
    const last = s.last
    return {
      state: 'expired' as HeroState,
      days: 0,
      until: last?.endsAt ?? null,
      start: last?.startsAt ?? null,
      ticksLeft: 0,
      devices: s.devices ?? last?.devices ?? null,
    }
  }
  const start = s.current?.startsAt ?? s.upcoming[0]?.startsAt ?? now
  const end = s.paidUntil
  const days = daysLeft(end, now)
  const share = Math.max(
    0,
    Math.min(1, (end.getTime() - now.getTime()) / (end.getTime() - start.getTime())),
  )
  // A plan bought during the trial already counts: the hero speaks about the paid time.
  const onTrialOnly = s.status === 'trial' && s.upcoming.length === 0
  const state: HeroState = onTrialOnly ? 'trial' : days <= 3 ? 'ending' : 'active'
  return { state, days, until: end, start, ticksLeft: Math.round(share * 30), devices: s.devices }
}
